import React, { useEffect, useState } from 'react';

function SummaryCard({ label, value, sub }) {
    return (
        <div className="bg-white rounded-xl shadow p-5 flex flex-col gap-1">
            <span className="text-sm text-gray-500">{label}</span>
            <span className="text-3xl font-bold text-gray-800">{value ?? '—'}</span>
            {sub && <span className="text-xs text-gray-400">{sub}</span>}
        </div>
    );
}

function CanalBadge({ web, tg }) {
    return (
        <div className="flex gap-1 flex-wrap">
            {web > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                    Web {web > 1 ? `×${web}` : ''}
                </span>
            )}
            {tg > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">
                    Telegram {tg > 1 ? `×${tg}` : ''}
                </span>
            )}
        </div>
    );
}

const SORT_OPTIONS = [
    { key: 'ultima_actividad', label: 'Última actividad' },
    { key: 'total_sesiones', label: 'Sesiones' },
    { key: 'mensajes_aprox', label: 'Mensajes' },
    { key: 'email', label: 'Email' },
];

export default function AdminAgentStatsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('ultima_actividad');
    const [sortDir, setSortDir] = useState('desc');

    useEffect(() => {
        fetch(`${window.REACT_APP_API_ENDPOINT}/api/agent/admin/stats`, { credentials: 'include' })
            .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
            .then(setData)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDir(key === 'email' ? 'asc' : 'desc');
        }
    };

    const sorted = data?.usuarios ? [...data.usuarios].sort((a, b) => {
        const av = a[sortBy] ?? '';
        const bv = b[sortBy] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
    }) : [];

    if (loading) return <div className="p-6 text-gray-500">Cargando estadísticas...</div>;
    if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

    const { summary } = data;

    return (
        <div className="max-w-6xl mx-auto mb-10">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Uso del Agente IA</h1>

            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <SummaryCard label="Usuarios activos" value={summary.usuarios_activos} sub="alguna vez" />
                <SummaryCard label="Activos hoy" value={summary.usuarios_hoy} />
                <SummaryCard label="Telegram vinculado" value={summary.telegram_vinculados} />
                <SummaryCard label="Sesiones totales" value={summary.total_sesiones} sub="días de uso" />
                <SummaryCard label="Mensajes aprox." value={summary.total_mensajes_aprox} sub="estimado" />
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Usuario</th>
                                <th className="px-4 py-3 text-left">Canales</th>
                                <th className="px-4 py-3 text-left">Telegram</th>
                                {SORT_OPTIONS.slice(1).map(o => (
                                    <th
                                        key={o.key}
                                        className="px-4 py-3 text-right cursor-pointer select-none hover:text-blue-600 whitespace-nowrap"
                                        onClick={() => handleSort(o.key)}
                                    >
                                        {o.label}
                                        {sortBy === o.key && (
                                            <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                ))}
                                <th
                                    className="px-4 py-3 text-right cursor-pointer select-none hover:text-blue-600 whitespace-nowrap"
                                    onClick={() => handleSort('ultima_actividad')}
                                >
                                    Última actividad
                                    {sortBy === 'ultima_actividad' && (
                                        <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(u => (
                                <tr key={u.id} className={`hover:bg-gray-50 ${u.activo_hoy ? 'bg-green-50' : ''}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <div className="font-medium text-gray-800 leading-tight">
                                                    {u.nombre || u.email}
                                                </div>
                                                {u.nombre && (
                                                    <div className="text-xs text-gray-400">{u.email}</div>
                                                )}
                                            </div>
                                            {u.activo_hoy && (
                                                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium ml-1">
                                                    Hoy
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <CanalBadge web={u.sesiones_web} tg={u.sesiones_tg} />
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {u.telegram_vinculado
                                            ? <span className="text-sky-600">@{u.telegram_username || 'vinculado'}</span>
                                            : <span className="text-gray-300">—</span>
                                        }
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                                        {u.total_sesiones}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                                        {u.mensajes_aprox}
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                                        {u.ultima_actividad ?? '—'}
                                    </td>
                                </tr>
                            ))}
                            {sorted.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                                        Ningún usuario ha usado el agente todavía
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                    Los mensajes son una estimación. Las filas en verde indican actividad hoy.
                </div>
            </div>
        </div>
    );
}
